/* eslint-disable prefer-destructuring */
/* eslint-disable max-statements */
import { nearestSixteenth, Vertex2 } from '@rta/rta-blueprint-library';

import { Vector } from '@r-core/types';

type Polygon = Array<Vector<Vertex2>>;
type FilledCell = `${number}:${number}`;

const toKey = (xi: number, yi: number): FilledCell => `${xi}:${yi}`;

const groupVectorsIntoPolygons = (vectors: Array<Vector<Vertex2>>): Array<Polygon> => {
	const polygons: Array<Polygon> = [];
	const used = new Set<number>();

	for (let i = 0; i < vectors.length; i++) {
		if (used.has(i)) continue;

		const polygon: Polygon = [];
		let current = vectors[i];
		polygon.push(current);
		used.add(i);

		let searching = true;
		while (searching) {
			searching = false;
			const targetX = nearestSixteenth(current.to.x);
			const targetY = nearestSixteenth(current.to.y);

			for (let j = 0; j < vectors.length; j++) {
				if (used.has(j)) continue;

				const fromX = nearestSixteenth(vectors[j].from.x);
				const fromY = nearestSixteenth(vectors[j].from.y);
				const toX = nearestSixteenth(vectors[j].to.x);
				const toY = nearestSixteenth(vectors[j].to.y);

				if (fromX === targetX && fromY === targetY) {
					current = vectors[j];
					polygon.push(current);
					used.add(j);
					searching = true;
					break;
				}

				if (toX === targetX && toY === targetY) {
					current = { ...vectors[j], from: vectors[j].to, to: vectors[j].from };
					polygon.push(current);
					used.add(j);
					searching = true;
					break;
				}
			}
		}

		polygons.push(polygon);
	}

	return polygons;
};

const isPointInPolygon = (point: Vertex2, vertices: Array<Vertex2>): boolean => {
	const px = point.x;
	const py = point.y;
	let inside = false;

	for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
		const xi = vertices[i].x;
		const yi = vertices[i].y;
		const xj = vertices[j].x;
		const yj = vertices[j].y;

		const onVertex = (xi === px && yi === py) || (xj === px && yj === py);
		if (onVertex) return true;

		const intersect = ((yi > py) !== (yj > py)) && (px < (((xj - xi) * (py - yi)) / (yj - yi)) + xi);
		if (intersect) inside = !inside;
	}

	return inside;
};

const polygonToVertices = (polygon: Polygon): Array<Vertex2> => {
	return polygon.map((edge) => new Vertex2({
		dataIn: {
			x: nearestSixteenth(edge.from.x),
			y: nearestSixteenth(edge.from.y)
		}
	}));
};

const collectGridLines = (polygons: Array<Polygon>): { xs: number[], ys: number[] } => {
	const xs = new Set<number>();
	const ys = new Set<number>();

	for (const poly of polygons) {
		for (const edge of poly) {
			xs.add(nearestSixteenth(edge.from.x));
			xs.add(nearestSixteenth(edge.to.x));
			ys.add(nearestSixteenth(edge.from.y));
			ys.add(nearestSixteenth(edge.to.y));
		}
	}

	const sortedXs = [...xs].sort((a, b) => a - b);
	const sortedYs = [...ys].sort((a, b) => a - b);

	return { xs: sortedXs, ys: sortedYs };
};

const mergeCollinearEdges = (edges: Array<Vector<Vertex2>>): Array<Vector<Vertex2>> => {
	const horizontals = new Map<number, Array<Vector<Vertex2>>>();
	const verticals = new Map<number, Array<Vector<Vertex2>>>();

	for (const edge of edges) {
		const fx = nearestSixteenth(edge.from.x);
		const fy = nearestSixteenth(edge.from.y);
		const tx = nearestSixteenth(edge.to.x);
		const ty = nearestSixteenth(edge.to.y);

		if (fy === ty) {
			const y = fy;
			const arr = horizontals.get(y) ?? [];
			arr.push({
				...edge,
				from: new Vertex2({ dataIn: { x: Math.min(fx, tx), y }}),
				to: new Vertex2({ dataIn: { x: Math.max(fx, tx), y }})
			});
			horizontals.set(y, arr);
		} else if (fx === tx) {
			const x = fx;
			const arr = verticals.get(x) ?? [];
			arr.push({
				...edge,
				from: new Vertex2({ dataIn: { x, y: Math.min(fy, ty) }}),
				to: new Vertex2({ dataIn: { x, y: Math.max(fy, ty) }})
			});
			verticals.set(x, arr);
		}
	}

	const merged: Array<Vector<Vertex2>> = [];

	for (const [, arr] of horizontals) {
		arr.sort((a, b) => a.from.x - b.from.x);
		let current = arr[0];
		for (let i = 1; i < arr.length; i++) {
			const next = arr[i];
			if (next.from.x <= current.to.x && next.to.x >= current.to.x) {
				current = { ...current, to: new Vertex2({ dataIn: { x: Math.max(current.to.x, next.to.x), y: current.to.y }}) };
			} else {
				merged.push(current);
				current = next;
			}
		}
		merged.push(current);
	}

	for (const [, arr] of verticals) {
		arr.sort((a, b) => a.from.y - b.from.y);
		let current = arr[0];
		for (let i = 1; i < arr.length; i++) {
			const next = arr[i];
			if (next.from.y <= current.to.y && next.to.y >= current.to.y) {
				current = { ...current, to: new Vertex2({ dataIn: { x: current.to.x, y: Math.max(current.to.y, next.to.y) }}) };
			} else {
				merged.push(current);
				current = next;
			}
		}
		merged.push(current);
	}

	return merged;
};

/**
 * Merge any overlapping/abutting mask polygons into a single (or minimal set of) closed-loop vectors.
 * Works on axis-aligned shapes (all existing mask vectors are snapped to nearestSixteenth).
 */
export const mergeOverlappingMaskLoops = (maskVectors: Array<Vector<Vertex2>>): Array<Vector<Vertex2>> => {
	if (maskVectors.length === 0) return maskVectors;

	const maskPolygons = groupVectorsIntoPolygons(maskVectors);
	if (maskPolygons.length <= 1) return maskVectors;

	const { xs, ys } = collectGridLines(maskPolygons);
	if (xs.length < 2 || ys.length < 2) return maskVectors;

	const filled = new Set<FilledCell>();
	for (let xi = 0; xi < xs.length - 1; xi++) {
		for (let yi = 0; yi < ys.length - 1; yi++) {
			const cx = (xs[xi] + xs[xi + 1]) / 2;
			const cy = (ys[yi] + ys[yi + 1]) / 2;
			const point = new Vertex2({ dataIn: { x: cx, y: cy }});

			const insideAny = maskPolygons.some((poly) => isPointInPolygon(point, polygonToVertices(poly)));
			if (insideAny) filled.add(toKey(xi, yi));
		}
	}

	const boundaryEdges: Array<Vector<Vertex2>> = [];
	for (let xi = 0; xi < xs.length - 1; xi++) {
		for (let yi = 0; yi < ys.length - 1; yi++) {
			if (!filled.has(toKey(xi, yi))) continue;

			const leftEmpty = !filled.has(toKey(xi - 1, yi));
			const rightEmpty = !filled.has(toKey(xi + 1, yi));
			const bottomEmpty = !filled.has(toKey(xi, yi - 1));
			const topEmpty = !filled.has(toKey(xi, yi + 1));

			const x0 = xs[xi];
			const x1 = xs[xi + 1];
			const y0 = ys[yi];
			const y1 = ys[yi + 1];

			const vCommon = { layer: maskVectors[0].layer, angle: 0, metadata: {}};
			// Clockwise edges with interior to the right
			if (leftEmpty) boundaryEdges.push({ ...vCommon, layer: maskVectors[0].layer, from: new Vertex2({ dataIn: { x: x0, y: y1 }}), to: new Vertex2({ dataIn: { x: x0, y: y0 }}) });
			if (bottomEmpty) boundaryEdges.push({ ...vCommon, layer: maskVectors[0].layer, from: new Vertex2({ dataIn: { x: x0, y: y0 }}), to: new Vertex2({ dataIn: { x: x1, y: y0 }}) });
			if (rightEmpty) boundaryEdges.push({ ...vCommon, layer: maskVectors[0].layer, from: new Vertex2({ dataIn: { x: x1, y: y0 }}), to: new Vertex2({ dataIn: { x: x1, y: y1 }}) });
			if (topEmpty) boundaryEdges.push({ ...vCommon, layer: maskVectors[0].layer, from: new Vertex2({ dataIn: { x: x1, y: y1 }}), to: new Vertex2({ dataIn: { x: x0, y: y1 }}) });
		}
	}

	// Collapse collinear fragments so we return tidy loops
	return mergeCollinearEdges(boundaryEdges);
};
